import { type FC, useEffect, useRef } from 'react';
import { useRefetchableFragment, graphql } from 'react-relay';
import { trackPageview } from 'dibs-tracking';
import { getBuyerId } from 'dibs-cookie-jar';

import { NftPdpLiveDetails } from './NftPdpLiveDetails';
import { NftPdpBidModalContextProvider } from '../NftPdpBidModal/NftPdpBidModalContext';

import { type NftPdpLiveDetailsRefetchWrapperQuery as NftPdpLiveDetailsRefetchWrapperQueryType } from './__generated__/NftPdpLiveDetailsRefetchWrapperQuery.graphql';
import { type NftPdpLiveDetailsRefetchWrapper_viewer$key } from './__generated__/NftPdpLiveDetailsRefetchWrapper_viewer.graphql';

type Props = {
    viewer: NftPdpLiveDetailsRefetchWrapper_viewer$key;
};

export const NftPdpLiveDetailsRefetchWrapper: FC<Props> = ({ viewer: viewerRef }) => {
    const [viewer, refetch] = useRefetchableFragment<
        NftPdpLiveDetailsRefetchWrapperQueryType,
        NftPdpLiveDetailsRefetchWrapper_viewer$key
    >(
        graphql`
            fragment NftPdpLiveDetailsRefetchWrapper_viewer on Viewer
            @refetchable(queryName: "NftPdpLiveDetailsRefetchWrapperQuery") {
                nftItems(nftIds: [$nftId], isDraft: $isDraft) @include(if: $fetchClientDetails) {
                    serviceId
                    mostRecentAuction {
                        status
                        currentBid {
                            wei
                        }
                    }
                    ...NftPdpLiveDetails_nftItem
                    ...NftPdpBidModalContext_nftItem
                }
                ...NftPdpBidModalContext_viewer
                ...NftPdpLiveDetails_viewer
            }
        `,
        viewerRef
    );

    useEffect(() => {
        const userId = getBuyerId(document.cookie);
        refetch({ fetchClientDetails: true, fetchUser: !!userId, userId });
    }, [refetch]);

    const { nftItems } = viewer || {};
    const nftItem = nftItems?.[0];

    const pageViewTrackingFiredRef = useRef(false);
    useEffect(() => {
        if (nftItem && !pageViewTrackingFiredRef.current) {
            const { status, currentBid } = nftItem.mostRecentAuction || {};
            let pageSubType;
            const isLive = status === 'LIVE';
            const isEnded = status === 'ENDED';
            if (isLive) {
                pageSubType = currentBid ? 'PDP-Available-Live-Bids-NFT' : 'PDP-Available-Live-NFT';
            } else if (isEnded) {
                pageSubType = 'PDP-Sold-NFT';
            } else {
                pageSubType = 'PDP-Upcoming-NFT';
            }

            trackPageview({
                additional: {
                    pageType: 'Products',
                    pageSubType,
                    itemId: nftItem.serviceId,
                },
            });
            pageViewTrackingFiredRef.current = true;
        }
    }, [nftItem]);

    if (!nftItem) {
        return null;
    }

    return (
        <NftPdpBidModalContextProvider nftItem={nftItem} viewer={viewer}>
            <NftPdpLiveDetails nftItem={nftItem} viewer={viewer} />
        </NftPdpBidModalContextProvider>
    );
};
